import {
  Checkbox,
  Group,
  NativeSelect,
  TextInput,
  ActionIcon,
  Stack,
  Button,
} from '@mantine/core';
import {
  IconSortAscending,
  IconSortDescending,
  IconX,
} from '@tabler/icons-react';
import { useIntl } from 'react-intl';
import { FilterField } from '@/users/pages/UserFilters/types/FilterField';

interface FilterControlsProps {
  filters: {
    isArchived: boolean;
    onlyWithData: boolean;
    backOfficeStatus: string;
    folderName: string;
    fileName: string;
    fileType: string;
    isPublished: string;
    isSigned: string;
    isLocked: string;
    fileStatus: string;
    [key: string]: any;
  };
  customFieldBasedFilters: FilterField[];
  setFilters: (filters: any) => void;
  resetPage: () => void;
  hasIsPublishedFiles: boolean;
  hasIsSignedFiles: boolean;
  hasIsLockedFiles: boolean;
  hasFileStatus: boolean;
}

export const FilterControls = ({
  filters,
  customFieldBasedFilters,
  setFilters,
  resetPage,
  hasIsPublishedFiles,
  hasIsSignedFiles,
  hasIsLockedFiles,
  hasFileStatus,
}: FilterControlsProps) => {
  const intl = useIntl();

  const handleArchiveChange = (checked: boolean) => {
    setFilters({ ...filters, isArchived: checked });
    resetPage();
  };

  const handleOnlyWithDataChange = (checked: boolean) => {
    setFilters({ ...filters, onlyWithData: checked });
    resetPage();
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters({ ...filters, backOfficeStatus: e.currentTarget.value });
    resetPage();
  };

  const handleFolderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, folderName: e.target.value });
    resetPage();
  };

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, fileName: e.target.value });
    resetPage();
  };

  const handleFileTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters({ ...filters, fileType: e.currentTarget.value });
    resetPage();
  };

  const handleIsPublishedChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters({ ...filters, isPublished: e.currentTarget.value });
    resetPage();
  };

  const handleIsSignedChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters({ ...filters, isSigned: e.currentTarget.value });
    resetPage();
  };

  const handleIsLockedChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters({ ...filters, isLocked: e.currentTarget.value });
    resetPage();
  };

  const handleFileStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters({ ...filters, fileStatus: e.currentTarget.value });
    resetPage();
  };

  const handleCustomFieldChange = (fieldName: string, value: string) => {
    setFilters({ ...filters, [fieldName]: value });
    resetPage();
  };

  const handleSort = (fieldName: string) => {
    const sortKey = `${fieldName}_sort`;
    const currentSort = filters[sortKey] || '';
    const nextSort =
      currentSort === 'asc' ? 'desc' : currentSort === 'desc' ? '' : 'asc';
    setFilters({ ...filters, [sortKey]: nextSort });
    resetPage();
  };

  const handleClearAllFilters = () => {
    // Build default filters object
    const defaultFilters: any = {
      isArchived: false,
      onlyWithData: false,
      backOfficeStatus: '',
      folderName: '',
      fileName: '',
      fileType: '',
      isPublished: '',
      isSigned: '',
      isLocked: '',
      fileStatus: '',
    };

    // Reset all custom field filters
    customFieldBasedFilters.forEach((field) => {
      defaultFilters[field.name] = '';
      // Also clear sort keys for custom fields
      defaultFilters[`${field.name}_sort`] = '';
    });

    // Clear any other sort keys that might exist
    Object.keys(filters).forEach((key) => {
      if (key.endsWith('_sort') && !defaultFilters.hasOwnProperty(key)) {
        defaultFilters[key] = '';
      }
    });

    setFilters(defaultFilters);
    resetPage();
  };

  // Check if any filter has a value
  const hasActiveFilters = () => {
    // Check boolean filters (isArchived, onlyWithData)
    if (filters.isArchived || filters.onlyWithData) {
      return true;
    }

    // Check string filters
    if (
      filters.backOfficeStatus ||
      filters.folderName?.trim() ||
      filters.fileName?.trim() ||
      filters.fileType ||
      filters.isPublished ||
      filters.isSigned ||
      filters.isLocked ||
      filters.fileStatus
    ) {
      return true;
    }

    // Check custom field filters
    for (const field of customFieldBasedFilters) {
      if (filters[field.name]?.trim()) {
        return true;
      }
      // Check sort keys for custom fields
      const sortKey = `${field.name}_sort`;
      if (filters[sortKey]) {
        return true;
      }
    }

    // Check for any other sort keys
    for (const key of Object.keys(filters)) {
      if (key.endsWith('_sort') && filters[key]) {
        return true;
      }
    }

    return false;
  };

  const filtersActive = hasActiveFilters();

  return (
    <Group gap="md" align="center">
      <Stack gap="md">
        {customFieldBasedFilters.length > 0 && (
          <Group gap="md">
            {customFieldBasedFilters.map((field) => {
              const sortKey = `${field.name}_sort`;
              const sortDirection = filters[sortKey] || '';

              return (
                <Group gap={4} key={field.name}>
                  <TextInput
                    placeholder={field.label}
                    value={filters[field.name] || ''}
                    onChange={(e) =>
                      handleCustomFieldChange(field.name, e.target.value)
                    }
                    type={field.type}
                    w={150}
                    size="xs"
                  />
                  <ActionIcon
                    variant={sortDirection ? 'filled' : 'light'}
                    color={sortDirection ? 'blue' : 'gray'}
                    size="sm"
                    onClick={() => handleSort(field.name)}
                  >
                    {sortDirection === 'desc' ? (
                      <IconSortDescending size={16} />
                    ) : (
                      <IconSortAscending size={16} />
                    )}
                  </ActionIcon>
                </Group>
              );
            })}
          </Group>
        )}

        <Group gap="md">
          <TextInput
            label={intl.formatMessage({ id: 'folderName' })}
            placeholder={intl.formatMessage({ id: 'folderName' })}
            value={filters.folderName || ''}
            onChange={handleFolderNameChange}
            w={150}
            size="xs"
          />

          <TextInput
            label={intl.formatMessage({ id: 'fileName' })}
            placeholder={intl.formatMessage({ id: 'fileName' })}
            value={filters.fileName || ''}
            onChange={handleFileNameChange}
            w={150}
            size="xs"
          />

          <NativeSelect
            label={intl.formatMessage({ id: 'fileType' })}
            value={filters.fileType || ''}
            onChange={handleFileTypeChange}
            data={[
              { value: '', label: intl.formatMessage({ id: 'all' }) },
              {
                value: 'CONTRACT',
                label: intl.formatMessage({ id: 'CONTRACT' }),
              },
              {
                value: 'ANNEX',
                label: intl.formatMessage({ id: 'ANNEX' }),
              },
            ]}
            w={150}
            size="xs"
          />
        </Group>

        <Group gap="md">
          <NativeSelect
            label={intl.formatMessage({ id: 'backOfficeStatus' })}
            value={filters.backOfficeStatus}
            onChange={handleStatusChange}
            data={[
              { value: '', label: intl.formatMessage({ id: 'all' }) },
              { value: '-1', label: intl.formatMessage({ id: 'noStatus' }) },
              { value: '3', label: intl.formatMessage({ id: 'inProgress' }) },
              { value: '4', label: intl.formatMessage({ id: 'finalized' }) },
            ]}
            w={200}
            size="xs"
          />

          {hasIsPublishedFiles && (
            <NativeSelect
              label={intl.formatMessage({ id: 'publishedStatus' })}
              value={filters.isPublished}
              onChange={handleIsPublishedChange}
              data={[
                { value: '', label: intl.formatMessage({ id: 'all' }) },
                {
                  value: 'true',
                  label: intl.formatMessage({ id: 'published' }),
                },
                {
                  value: 'false',
                  label: intl.formatMessage({ id: 'notPublished' }),
                },
              ]}
              w={200}
              size="xs"
            />
          )}

          {hasIsSignedFiles && (
            <NativeSelect
              label={intl.formatMessage({ id: 'signedStatus' })}
              value={filters.isSigned}
              onChange={handleIsSignedChange}
              data={[
                { value: '', label: intl.formatMessage({ id: 'all' }) },
                { value: 'true', label: intl.formatMessage({ id: 'signed' }) },
                {
                  value: 'false',
                  label: intl.formatMessage({ id: 'notSigned' }),
                },
              ]}
              w={200}
              size="xs"
            />
          )}

          {hasIsLockedFiles && (
            <NativeSelect
              label={intl.formatMessage({ id: 'lockedStatus' })}
              value={filters.isLocked}
              onChange={handleIsLockedChange}
              data={[
                { value: '', label: intl.formatMessage({ id: 'all' }) },
                { value: 'true', label: intl.formatMessage({ id: 'locked' }) },
                {
                  value: 'false',
                  label: intl.formatMessage({ id: 'notLocked' }),
                },
              ]}
              w={200}
              size="xs"
            />
          )}

          {hasFileStatus && (
            <NativeSelect
              label={intl.formatMessage({ id: 'fileStatus' })}
              value={filters.fileStatus}
              onChange={handleFileStatusChange}
              data={[
                { value: '', label: intl.formatMessage({ id: 'all' }) },
                { value: '0', label: intl.formatMessage({ id: 'new' }) },
                { value: '1', label: intl.formatMessage({ id: 'draft' }) },
                { value: '2', label: intl.formatMessage({ id: 'confirmed' }) },
                { value: '3', label: intl.formatMessage({ id: 'inProgress' }) },
                { value: '4', label: intl.formatMessage({ id: 'finalized' }) },
              ]}
              w={200}
              size="xs"
            />
          )}
        </Group>

        <Group gap="md">
          <Checkbox
            label={intl.formatMessage({ id: 'isArchived' })}
            checked={filters.isArchived}
            onChange={(e) => handleArchiveChange(e.currentTarget.checked)}
          />

          <Checkbox
            label={intl.formatMessage({ id: 'onlyWithData' })}
            checked={filters.onlyWithData}
            onChange={(e) => handleOnlyWithDataChange(e.currentTarget.checked)}
          />
        </Group>

        <Group gap="md" align="flex-start">
          <Button
            variant="light"
            size="xs"
            leftSection={<IconX size={16} />}
            onClick={handleClearAllFilters}
            disabled={!filtersActive}
            aria-hidden={!filtersActive}
            tabIndex={filtersActive ? 0 : -1}
            style={
              filtersActive
                ? undefined
                : { visibility: 'hidden', pointerEvents: 'none' }
            }
          >
            {intl.formatMessage({ id: 'clearAllFilters' })}
          </Button>
        </Group>
      </Stack>
    </Group>
  );
};
